import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import Dotdotdot from 'react-dotdotdot';
import {FormattedMessage, FormattedNumber} from 'react-intl';

import {Panel} from '@stubhub/bamboo';
// eslint-disable-next-line import/no-named-as-default
import DateStamp from '@stubhub/react-date-stamp';
import {getEventTime, getEventDate, getVenueInfo, getTicketLeftInfo} from '@stubhub/react-event-helper';
import EventRedirection from '@stubhub/react-event-redirection';
import EventUrgency from '@stubhub/react-event-urgency';
import FormattedLink from '@stubhub/react-formatted-link';
import {connect} from '@stubhub/react-store-provider';
import {theme} from '@stubhub/react-theme-provider';
import urgency from '@stubhub/react-urgency-decorator';

import './index.scss';

const viewTicketsButtonText = <FormattedMessage id="EventItem.ViewTicket.ButtonText" defaultMessage="View tickets" />;

// Cxs is mandatory here, cannot test branch coverage
/* istanbul ignore next */
const themeLinkColorClassName = (theme = {}, cxs) =>
  theme.keyTextLinks &&
  cxs &&
  cxs({
    color: `${theme.keyTextLinks} !important`,
    fontWeight: 500,
  });

// Cxs is mandatory here, cannot test branch coverage
/* istanbul ignore next */
const themeHoverColorClassName = (theme = {}, cxs) =>
  theme.paletteFocus &&
  cxs &&
  cxs({
    ':hover': {
      background: `${theme.paletteFocus} !important`,
    },
  });

// Cxs is mandatory here, cannot test branch coverage
/* istanbul ignore next */
const themeTicketLeftClassName = (theme = {}, cxs) =>
  theme.palettePrimary &&
  cxs &&
  cxs({
    color: `${theme.palettePrimary} !important`,
  });

const themeTicketPriceClassName = (theme, cxs) => themeTicketLeftClassName(theme, cxs);

function mapStateToProps(state) {
  let deviceState = state.device || {};

  return {
    deviceType: deviceState.type,
  };
}

@theme
@urgency
class EventItem extends Component {
  static propTypes = {
    theme: PropTypes.object,
    event: PropTypes.object,
    threshold: PropTypes.number,
    type: PropTypes.string,
    urgencyConfig: PropTypes.object,

    /**
     * ThemeProvider object for applying the theme.
     *
     */
    themeProvider: PropTypes.func.isRequired,

    /**
     * Flag to Enable the View Tickets Feature on Phones Only.
     *
     */
    enableViewTickets: PropTypes.bool,

    /**
     * Options Containing the Parts of the Event Items to be Hidden
     *
     */
    eventItemDisplayOptions: PropTypes.shape({
      disableTicketsLeft: PropTypes.bool,
      disableDateStamp: PropTypes.bool,
      disableVenueAndTime: PropTypes.bool,
      disablePrice: PropTypes.bool,
      className: PropTypes.string,
    }),
  };

  static defaultPropTypes = {
    eventItemDisplayOptions: {
      disableTicketsLeft: false,
      disableDateStamp: false,
      disableVenueAndTime: false,
      disablePrice: false,
      className: '',
    },
  };

  static contextTypes = {
    urgency: PropTypes.object,
  };

  /**
   * Function to Handle Click on Venue Link
   * @param evt React Event Object for the `click` action.
   */
  navigateToVenuePage = (evt) => {
    /* istanbul ignore if */
    if (evt) {
      evt.stopPropagation();
    }
  };

  navigateTo = (e) => {
    e && e.stopPropagation();
    e && e.preventDefault();
    let links = this.mainNode.getElementsByClassName('EventItem__TitleLink');
    if (links && links[0]) {
      links[0].click();
    }
  };

  /**
   * React Lifecycle Methods
   *
   */
  render() {
    let {
      event,
      threshold,
      type = 'default',
      theme,
      themeProvider,
      urgencyConfig,
      deviceType,
      enableViewTickets,
      eventItemDisplayOptions = {},
    } = this.props;

    /**
     * Event Item Display Options to Control the Visibility and Styling of the Event item.
     *
     */
    let {
      disableDateStamp,
      disableVenueAndTime,
      disableTicketsLeft,
      disablePrice,
      className: eventItemClassName,
      hideBorder,
    } = eventItemDisplayOptions;
    let urgencyData = this.context.urgency;
    let {name, ticketInfo = {}, urgencyMessage} = event;
    let urgencyPopular = urgencyData && urgencyData.popularEvents;

    /**
     * Event Date and Time Section
     *
     */
    let eventDate = getEventDate(event);
    let timeDisplay = getEventTime(event);

    /**
     * Event Venue Section
     *
     */
    let infoDisplay = null;
    if (!disableVenueAndTime) {
      let {venueLocation, venueName, venueWebURI} = getVenueInfo(event);
      let venueDisplay;
      if (venueName && venueWebURI && type === 'default') {
        venueDisplay = (
          <span className="EventItem__VenueInfo">
            <FormattedLink
              target="_blank"
              onClick={this.navigateToVenuePage}
              href={`/${venueWebURI}`}
              className={`EventItem__link--Venue ${themeLinkColorClassName(theme, themeProvider)}`}
            >
              {venueName}
            </FormattedLink>
            , {venueLocation}
          </span>
        );
      } else {
        venueDisplay = <span className="EventItem__VenueInfo">{venueName}</span>;
      }
      infoDisplay = (
        <div className="EventItem__MixInfo">
          <span>
            <time>{timeDisplay}</time> - {venueDisplay}
          </span>
        </div>
      );
    }

    /**
     * Event Price Section
     * TODO - it may depends on other things for show tickets left info (some countries only)
     *
     */
    let {totalTickets, currencyCode, minListPrice} = ticketInfo;

    /**
     * Show Tickets left urgency is one of two variants for displaying tickets
     *  remaining under the threshold given
     *
     */
    let showTicketsLeftUrgency =
      urgencyConfig && urgencyConfig.ticketsLeft && totalTickets > 0 && totalTickets < threshold;
    if (showTicketsLeftUrgency && urgencyMessage === undefined) {
      urgencyMessage = {ticketsLeft: totalTickets};
    }
    if (urgencyPopular && urgencyConfig && urgencyConfig.sellingFast && urgencyPopular.indexOf(event.id) > -1) {
      urgencyMessage = {sellingFast: true};
    }

    /**
     * Show Urgency Message if the config Exist and th urgency message is present.
     *
     */
    let showUrgency = urgencyMessage && urgencyConfig;

    /**
     * Tickets left is the control variant and should only display
     *  when that urgency is false or zero tickets remain
     *
     */
    let ticketsLeftDisplay =
      !showTicketsLeftUrgency && urgencyMessage === undefined && getTicketLeftInfo(event, threshold);
    let ticketLeftSection =
      !disableTicketsLeft && ticketsLeftDisplay ? (
        <div
          className={`EventItem__Quantity ${themeTicketLeftClassName(theme, themeProvider)} ${
            totalTickets === 0 ? 'EventItem__Quantity-none' : ''
          }`}
        >
          {ticketsLeftDisplay}
        </div>
      ) : null;

    /**
     * Show View Tickets only on `phones` if the toggle is on and there is tickets for that event.
     *
     */
    let showViewTickets = enableViewTickets && deviceType === 'phone' && totalTickets > 0;

    /**
     * If View Tickets is enabled then donot show the the Price section
     *
     */
    let showPriceInfo = !disablePrice && !showViewTickets && type === 'default' && totalTickets > 0;
    let ticketsDisplay = showPriceInfo ? (
      <div className="EventItem__Tickets">
        <div className="EventItem__From">
          <FormattedMessage id="EventItem.priceFrom" defaultMessage="from" />
        </div>

        {
          <div className={`EventItem__Price ${themeTicketPriceClassName(theme, themeProvider)}`}>
            <FormattedNumber
              style="currency" //eslint-disable-line
              maximumFractionDigits={0}
              minimumFractionDigits={0}
              currencyDisplay="symbol"
              currency={currencyCode}
              useGrouping
              value={minListPrice}
            />
          </div>
        }
      </div>
    ) : null;

    /**
     * Event Item Body Section
     *
     */
    let content = (
      <div
        className={cs('EventItem__Body', themeHoverColorClassName(theme, themeProvider))}
        ref={(node) => (this.mainNode = node)}
        onClick={(e) => this.navigateTo(e)}
      >
        <div className="EventItem__Info">
          <div className="EventItem__Date">{!disableDateStamp ? <DateStamp date={eventDate} /> : null}</div>
          <div className="EventItem__Details">
            <div className="EventItem__EventInfo">
              <div className="EventItem__Title" style={{wordWrap: 'break-words'}} onClick={(e) => e.stopPropagation()}>
                <EventRedirection
                  className={cs('EventItem__TitleLink', themeLinkColorClassName(theme, themeProvider))}
                  event={event}
                  onClick={this.props.onClick}
                >
                  <Dotdotdot clamp={2}>{name}</Dotdotdot>
                </EventRedirection>
              </div>
              {infoDisplay}
              {showViewTickets && <div className="EventItem__ViewTickets">{viewTicketsButtonText}</div>}
            </div>
            {ticketsDisplay}
          </div>
        </div>
        {(showUrgency || ticketLeftSection) && (
          <div className="EventItem__Urgency">
            {showUrgency ? (
              <EventUrgency urgencyMessage={urgencyMessage} urgencyConfig={urgencyConfig} />
            ) : (
              ticketLeftSection
            )}
          </div>
        )}
      </div>
    );

    return <Panel className={cs(eventItemClassName || 'EventItem')} content={content} border={!hideBorder} />;
  }
}

export default connect({mapStateToProps})(EventItem);
